import React, { useEffect, useRef, useState } from 'react'
import {
  Typography,
  Button,
  Breadcrumb,
  Layout,
  Flex,
  Card,
  Form,
  Input,
  Spin,
  message,
} from 'antd'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { mockCampaigns } from '@/data/smsRemarketing'
// import { getTemplate } from '@/api/lop/campaigns/templates/getTemplate'
// import { createTemplate } from '@/api/lop/campaigns/templates/createTemplate'
// import { updateTemplate } from '@/api/lop/campaigns/templates/updateTemplate'

const { Title } = Typography
const { Content } = Layout
const { TextArea } = Input

const SmsTemplateEditorContent: React.FC = () => {
  const navigate = useNavigate()
  const { id: campaignId, templateId } = useParams<{
    id: string
    templateId: string
  }>()
  const isEditMode = Boolean(templateId)

  const [loading, setLoading] = useState(isEditMode)
  const [saving, setSaving] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [templateText, setTemplateText] = useState('')

  const hasFetched = useRef(false)

  useEffect(() => {
    if (!campaignId || !templateId || hasFetched.current) return
    hasFetched.current = true

    // remove this block and uncomment real API below
    const campaign = mockCampaigns.find((c) => String(c.id) === campaignId)
    const template = campaign?.campaign_templates?.find(
      (t) => String(t.id) === templateId,
    )
    if (template) {
      setTemplateName(template.name)
      setTemplateText(template.text)
    }
    setLoading(false)
    // ;(async () => {
    //   try {
    //     setLoading(true)
    //     const template = await getTemplate(campaignId, templateId)
    //     if (template) {
    //       setTemplateName(template.name)
    //       setTemplateText(template.text)
    //     }
    //   } catch (err) {
    //     message.error('Failed to load template')
    //     console.error(err)
    //   } finally {
    //     setLoading(false)
    //   }
    // })()
  }, [campaignId, templateId])

  const handleSave = async () => {
    if (!campaignId) return
    if (!templateName.trim()) {
      message.warning('Please enter a template name')
      return
    }
    if (!templateText.trim()) {
      message.warning('Please enter message text')
      return
    }
    try {
      setSaving(true)

      //remove this block and uncomment real API below
      await new Promise((r) => setTimeout(r, 500))
      message.success(isEditMode ? 'Template updated' : 'Template created')
      if (!isEditMode) {
        navigate(`/sms-remarketing/${campaignId}/edit`)
      }

      // const payload = { name: templateName, text: templateText }
      // if (isEditMode && templateId) {
      //   await updateTemplate(campaignId, templateId, payload)
      //   message.success('Template updated')
      // } else {
      //   const ordinal =
      //     Number(new URLSearchParams(window.location.search).get('ordinal')) ||
      //     1
      //   const created = await createTemplate(campaignId, {
      //     ...payload,
      //     ordinal,
      //   })
      //   message.success('Template created')
      //   navigate(`/sms-remarketing/${campaignId}/templates/${created.id}/edit`)
      // }
    } catch {
      message.error(
        isEditMode ? 'Failed to update template' : 'Failed to create template',
      )
      // console.error(err)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 400 }}>
        <Spin size="large" />
      </Flex>
    )
  }

  return (
    <div>
      <Flex vertical gap="middle" style={{ width: '100%' }}>
        <Breadcrumb
          items={[
            { title: 'SMS Remarketing', href: '/sms-remarketing' },
            {
              title: 'Campaign',
              href: `/sms-remarketing/${campaignId}/edit`,
            },
            {
              title: isEditMode ? `Edit: ${templateName}` : 'New Template',
            },
          ]}
        />

        <Flex align="center" gap="middle">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(`/sms-remarketing/${campaignId}/edit`)}
          />
          <Title level={3} style={{ margin: 0 }}>
            {isEditMode
              ? `Edit Template: ${templateName}`
              : 'Create New Template'}
          </Title>
        </Flex>

        <Flex gap="large" style={{ marginTop: 16 }}>
          <Content style={{ flex: 1, minHeight: 400 }}>
            <Card title="Template Info" style={{ maxWidth: 1200 }}>
              <Form layout="vertical">
                <Form.Item label="Template Name">
                  <Input
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="Enter template name"
                  />
                </Form.Item>
                <Form.Item label="Message Text">
                  <div style={{ paddingBottom: 24 }}>
                    <TextArea
                      value={templateText}
                      onChange={(e) => setTemplateText(e.target.value)}
                      placeholder="Enter SMS message text..."
                      rows={4}
                      showCount
                      maxLength={320}
                      styles={{ count: { bottom: '-30px' } }}
                    />
                  </div>
                </Form.Item>
              </Form>
              <Flex justify="flex-end">
                <Button type="primary" onClick={handleSave} loading={saving}>
                  {isEditMode ? 'Save Changes' : 'Create Template'}
                </Button>
              </Flex>
            </Card>
          </Content>
        </Flex>
      </Flex>
    </div>
  )
}

export const SmsTemplateEditor: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>()
  return <SmsTemplateEditorContent key={templateId ?? 'new'} />
}
